import { SOFIA_PANEL_STACKING_CLASS } from "@/constants/sofia"

/**
 * Panel z-layering. The Sofia panel is an opaque, fixed `<aside>` that sits
 * below the `@coval/ui` overlay layer, so every Radix overlay the panel spawns
 * (the summary-memory popover, the expanded-chart dialog, tooltips) portals its
 * content to `<body>` at the design-system default `z-50` and paints above the
 * panel with no help from the caller.
 *
 * The layer lives in the shared lane because surfaces outside this module are
 * layered against the panel too, and a second copy of the number would drift.
 */
export const PANEL_STACKING_CLASS = SOFIA_PANEL_STACKING_CLASS

/**
 * The resize grip's layer. The grip is a fixed sibling of the panel rather than
 * a portal, so nothing lifts it for free: it needs to sit one step above the
 * panel to stay grabbable where the two overlap, while staying under the
 * overlay layer so a dialog still covers it.
 */
export const PANEL_HANDLE_STACKING_CLASS = "z-[46]"

/**
 * Sofia's proposed-action contract: every action kind the panel
 * knows how to render (and maybe execute). The wire shape lives in
 * `model/types.ts`; parsing/labeling helpers live in `utils/actions.ts`.
 */
export const ACTION_KINDS = [
  "create_alert",
  "update_alert",
  "delete_alert",
  "test_alert",
  "launch_run",
  "rerun_run",
  "rerun_failed",
  "cancel_run",
  "attach_metrics_to_agent",
  "duplicate_persona",
  "duplicate_agent",
  "duplicate_test_set",
  "create_agent",
  "create_test_set",
  "add_test_case",
  "draft_test_cases",
  "create_persona",
  "create_metric",
  "create_dashboard",
  "create_report",
  "create_monitor",
  "create_widget",
  "build_dashboard",
  "create_review_project",
  "add_run_to_review_project",
  "add_simulations_to_review_project",
  "remove_simulations_from_review_project",
  "update_agent",
  "delete_agent",
  "update_persona",
  "delete_persona",
  "update_metric",
  "delete_metric",
  "update_dashboard",
  "delete_dashboard",
  "update_report",
  "delete_report",
  "update_monitor",
  "delete_monitor",
  "update_run",
  "update_test_set",
  "update_test_case",
  "update_review_project",
  "delete_test_set",
  "delete_test_case",
  "delete_review_project",
  "navigate",
  "render_visualization",
] as const

export type SofiaActionKind = (typeof ACTION_KINDS)[number]

/**
 * `navigate` destinations — kept in LOCKSTEP with sofia-agent's
 * `NAVIGATE_DESTINATIONS` (src/sofia_agent/product/actions.py). The
 * server validates the contract (enum membership + conditional resource_id);
 * the FE owns the destination → org-scoped route mapping (see utils/actions.ts).
 */
export const NAVIGATE_LIST_DESTINATIONS = [
  "home",
  // Sofia's Investigations page (/home/investigations): the full list of
  // scheduled-investigation findings behind the Home daily briefing.
  "investigations",
  "dashboards",
  "metrics",
  "reports",
  "alerts",
  "alert_create",
  "agents",
  "agent_create",
  "personas",
  "test_sets",
  "templates",
  "scheduled",
  "simulated_conversations",
  "uploaded_conversations",
  "simulations",
  "runs",
  "conversations",
  "trace_search",
  "human_review",
  "simulate",
  // Org settings (members, API keys, integrations) — a list-type page, no id.
  "settings",
] as const

/**
 * Detail destinations open a single resource and REQUIRE `resource_id`.
 * `conversation` (singular) opens ONE monitoring conversation by its
 * simulation-output id — distinct from the `conversations` list page above.
 * See `navigateRelativePath` for the /uploaded/results/{id} route.
 */
export const NAVIGATE_DETAIL_DESTINATIONS = [
  "alert",
  "alert_edit",
  "test_set",
  "run",
  "report",
  "persona",
  "metric",
  "agent",
  "template",
  "simulated_conversation",
  "uploaded_conversation",
  "conversation",
  // One simulation's result detail, by simulation_output_id.
  "simulation",
  // One simulation's captured trace view, by simulation_output_id. Sofia only
  // proposes this when traces were actually SENT for that simulation (the
  // traces-only-if-sent gate lives in the charter, sofia-agent instructions.py);
  // the route below points at the per-simulation external trace viewer.
  "simulation_trace",
  "human_review_project",
  // One scheduled-investigation finding, by finding id: the Investigations page
  // with that row focused (`?finding_id=`), the deep link the Slack briefs use.
  "investigation",
  // One dashboard by id — where Sofia lands the user after `build_dashboard`.
  "dashboard",
] as const

export const NAVIGATE_DESTINATIONS = [
  ...NAVIGATE_LIST_DESTINATIONS,
  ...NAVIGATE_DETAIL_DESTINATIONS,
] as const

export type NavigateDestination = (typeof NAVIGATE_DESTINATIONS)[number]

/**
 * Kinds that EXECUTE the moment they arrive — no confirm card, no countdown.
 * `navigate` is non-destructive and reversible via browser back;
 * `render_visualization` draws pixels from a validated spec and mutates
 * nothing. The server stamps `requires_confirmation=false` on both. This is an
 * explicit allowlist so any future kind defaults to manual confirmation.
 */
export const AUTO_EXECUTE_KINDS: readonly SofiaActionKind[] = [
  "navigate",
  "render_visualization",
]

/**
 * The low-cost / low-risk write kinds — a create-or-clone that produces a
 * single inert, deletable resource. Two jobs: with the Sofia redesign ON this
 * only classifies proposals in the usage analytics funnel
 * (`auto_confirmable`) and every write still needs a manual confirm; with it
 * OFF this is drive mode's auto-confirm allowlist. Either way it is an explicit
 * list, so a new kind defaults to manual confirmation until added deliberately.
 * Simulation launches/re-runs consume resources, so they stay excluded even
 * though they are non-destructive.
 */
export const AUTO_CONFIRMABLE_KINDS: readonly SofiaActionKind[] = [
  "attach_metrics_to_agent",
  "create_agent",
  "create_test_set",
  "add_test_case",
  "draft_test_cases",
  "create_persona",
  "create_dashboard",
  "create_report",
  "create_monitor",
  "create_widget",
  // A whole board of inert, deletable widgets — the same profile as one widget,
  // just more of them, and never a paid simulation.
  "build_dashboard",
  // A clone is the cheapest write there is: one new inert resource, deletable,
  // costing nothing to run. Same low-risk profile as create_persona.
  "duplicate_test_set",
  "duplicate_persona",
  "duplicate_agent",
]

/**
 * Modify kinds (`update_*`): change an existing resource in place. They require
 * a manual confirm with a "what changes" preview.
 */
export const MODIFY_KINDS: readonly SofiaActionKind[] = [
  "update_alert",
  "update_agent",
  "update_persona",
  "update_metric",
  "update_dashboard",
  "update_report",
  "update_monitor",
  "update_run",
  "update_test_set",
  "update_test_case",
  "update_review_project",
  "add_run_to_review_project",
  "add_simulations_to_review_project",
  "remove_simulations_from_review_project",
]

/**
 * Destructive kinds (`delete_*`): remove a resource. They require a manual
 * confirm rendered with danger styling and consequence-specific copy. Agent
 * deletion is the one recoverable soft-delete.
 */
export const DESTRUCTIVE_KINDS: readonly SofiaActionKind[] = [
  "delete_alert",
  // Stopping a run is not a delete, but it is irreversible in the same way: a
  // stopped run can only be launched again, never resumed. Mirrors
  // `destructive=True` on sofia-agent's cancel_run spec.
  "cancel_run",
  "delete_agent",
  "delete_persona",
  "delete_metric",
  "delete_dashboard",
  "delete_report",
  "delete_monitor",
  "delete_test_set",
  "delete_test_case",
  "delete_review_project",
]

/**
 * Kinds executed SERVER-SIDE via the public /v1 API: on confirm the panel
 * echoes the signed `ActionConfirmation` back through the chat proxy and Sofia
 * runs the mapped /v1 op with the user's managed API key. Everything NOT listed
 * here executes client-side via the user's transport session (useActionExecutor) —
 * the existing launch/rerun/attach writes and navigate. This is the seam the rest
 * of the FE writes migrate across once the channel is proven.
 */
export const SERVER_EXECUTED_KINDS: readonly SofiaActionKind[] = [
  "create_alert",
  "update_alert",
  "delete_alert",
  "test_alert",
  "rerun_failed",
  "cancel_run",
  "duplicate_persona",
  "duplicate_agent",
  "duplicate_test_set",
  "create_agent",
  "create_test_set",
  "add_test_case",
  "create_persona",
  "create_metric",
  "create_dashboard",
  "create_report",
  "create_monitor",
  "create_widget",
  "build_dashboard",
  "update_agent",
  "delete_agent",
  "update_persona",
  "delete_persona",
  "update_metric",
  "delete_metric",
  "update_dashboard",
  "delete_dashboard",
  "update_report",
  "delete_report",
  "update_monitor",
  "delete_monitor",
  "update_run",
  "update_test_set",
  "update_test_case",
  "delete_test_set",
  "delete_test_case",
  "create_review_project",
  "add_run_to_review_project",
  "add_simulations_to_review_project",
  "remove_simulations_from_review_project",
  "update_review_project",
  "delete_review_project",
]

/**
 * Hard cap on proposals accepted per turn — keeps a pathological reply from
 * flooding the thread. Matches Sofia's practical create_metric batch limit.
 */
export const MAX_PROPOSED_ACTIONS_PER_TURN = 8

/** Default iteration count for a re-run when Sofia doesn't specify one. */
export const RERUN_DEFAULT_ITERATIONS = 3

/**
 * Default iteration count for a scoped `rerun_failed`. Lower than a whole-run
 * re-run: this one already targets the specific cases in question, so one pass
 * is the useful default. Matches sofia-agent's spec default.
 */
export const RERUN_FAILED_DEFAULT_ITERATIONS = 1

/**
 * Cap on the case list a `rerun_failed` may carry, mirroring sofia-agent's
 * `max_items` and the backend's own limit on `options.test_case_ids`.
 */
export const MAX_RERUN_FAILED_TEST_CASES = 100
